import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';

import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { usersApi } from '@/api/users';
import { humanizeError } from '@/lib/errors';

/**
 * Заявка на пробное занятие.
 *
 * ⚠️ ЭТО НЕ ПОКУПКА, и в этом весь смысл. Раньше кнопки вели в WhatsApp, а в
 * приложении из магазина такую кнопку приходится прятать: призыв купить мимо
 * Apple там запрещён. Получалось, что установивший приложение человек не мог
 * обратиться к нам вообще никак.
 *
 * Заявка на занятие с живым преподавателем покупкой не является — её прятать
 * не нужно. Поэтому здесь НЕТ и не должно появиться: цен, тарифов, слова
 * «оплата» и ссылок наружу. Как только появится одно из этого, форма
 * превратится в то самое, чего мы избегаем.
 *
 * ВОЗРАСТ И РОДИТЕЛЬ. Спрашиваем возраст, и если человеку меньше 18 — просим
 * контакты родителя. Наша политика конфиденциальности обещает не собирать
 * данные несовершеннолетних без законного представителя, а спросить возраст
 * платформа до 31.08.2026 не умела вовсе.
 */
export function LeadModal({ about, onClose }: { about?: string; onClose: () => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [age, setAge] = useState('');
  const [parentName, setParentName] = useState('');
  const [parentPhone, setParentPhone] = useState('');
  const [done, setDone] = useState(false);

  const n = Number(age);
  const minor = age !== '' && Number.isFinite(n) && n > 0 && n < 18;
  const digits = (s: string) => s.replace(/\D+/g, '').length;

  const ready =
    name.trim().length >= 2 &&
    digits(phone) >= 10 &&
    Number.isFinite(n) &&
    n >= 5 &&
    n <= 100 &&
    (!minor || (parentName.trim().length >= 2 && digits(parentPhone) >= 10));

  const send = useMutation({
    mutationFn: () =>
      usersApi.createLead({
        name: name.trim(),
        phone: phone.trim(),
        about,
        age: n,
        parent_name: minor ? parentName.trim() : undefined,
        parent_phone: minor ? parentPhone.trim() : undefined,
      }),
    onSuccess: () => setDone(true),
  });

  return (
    <Modal open onClose={onClose} title={t('lead.title')}>
      {done ? (
        <div className="space-y-4">
          <p className="text-sm text-on-surface">{t('lead.doneText')}</p>
          <div className="flex justify-end">
            <Button onClick={onClose}>{t('common.close')}</Button>
          </div>
        </div>
      ) : (
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (ready && !send.isPending) send.mutate();
          }}
        >
          <p className="text-sm text-on-surface-variant">{t('lead.hint')}</p>

          <Input label={t('lead.name')} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          <Input
            label={t('lead.phone')}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+7 700 000 00 00"
            inputMode="tel"
          />
          <Input
            label={t('lead.age')}
            value={age}
            onChange={(e) => setAge(e.target.value.replace(/\D+/g, '').slice(0, 3))}
            inputMode="numeric"
            placeholder="18"
          />

          {/* Поля родителя появляются сами, когда возраст меньше 18: спрашивать
              их у взрослого незачем, а прятать от школьника нельзя. */}
          {minor && (
            <>
              <p className="text-sm text-on-surface-variant">{t('lead.parentHint')}</p>
              <Input label={t('lead.parentName')} value={parentName} onChange={(e) => setParentName(e.target.value)} />
              <Input
                label={t('lead.parentPhone')}
                value={parentPhone}
                onChange={(e) => setParentPhone(e.target.value)}
                placeholder="+7 700 000 00 00"
                inputMode="tel"
              />
            </>
          )}

          {send.error && <p className="text-sm text-error">{humanizeError(send.error)}</p>}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={!ready} loading={send.isPending}>
              {t('lead.submit')}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
